import React from 'react';
import { Box, MenuItem, TextField, Typography } from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { VOLABY_REGION } from '../../../../trial-screens/create-trial-account/CreateTrialAccountConsts';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateSubAccountDialog = (props: Props) => {
  const { open, onClose, onCreated } = props;
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [name, setName] = React.useState('');
  const [region, setRegion] = React.useState(VOLABY_REGION[0].value);
  const [saving, setSaving] = React.useState(false);

  const reset = () => {
    setName('');
    setRegion(VOLABY_REGION[0].value);
    setSaving(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = () => {
    setSaving(true);

    campfireMutation
      .run({
        url: `${apiUrl}/tm/enterprise/sub-accounts/create`,
        method: 'post',
        data: { name: name.trim(), region },
      })
      .then((res: any) => {
        if (!res.ok) {
          setSaving(false);
          setSnackbar({
            variant: 'error',
            open: true,
            message: res?.data?.message || 'Could not create sub-account',
          });
          return;
        }

        setSnackbar({
          variant: 'success',
          open: true,
          message: `${name.trim()} created`,
        });
        reset();
        onCreated();
        onClose();
      })
      .catch(() => {
        setSaving(false);
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Could not create sub-account',
        });
      });
  };

  if (!open) return null;

  return (
    <FullscreenDialog
      open
      close={handleClose}
      title='Create Sub-Account'
      maxWidth='xs'
      fullWidth
      dialogActions={() => (
        <>
          <TabletButton onClick={handleClose} disabled={saving} style={{ marginRight: '8px' }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleCreate}
            disabled={saving || name.trim() === ''}
          >
            {saving ? 'Creating…' : 'Create'}
          </TabletButton>
        </>
      )}
    >
      <Box mt='20px'>
        <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
          {
            'This creates a new Volaby account beneath yours. It starts empty, so you can copy entities across and invite its admins afterwards.'
          }
        </Typography>

        <Box marginTop='1.25rem'>
          <TextField
            autoFocus
            fullWidth
            required
            variant='outlined'
            label='Sub-account name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText='Used for the account name and its web address'
          />
        </Box>

        <Box marginTop='1.25rem'>
          <TextField
            select
            fullWidth
            variant='outlined'
            label='Region'
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            helperText='Sets address lookup and formatting for this sub-account'
          >
            {VOLABY_REGION.map((regionOption) => (
              <MenuItem key={regionOption.value} value={regionOption.value}>
                {regionOption.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </FullscreenDialog>
  );
};
